import { Loading } from "@podtrac/common/components/Loading";
import { scaleOrdinal, schemeCategory10 } from "d3";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import { DownloadsLabel } from "../shared/DownloadsLabel";
import ExportButton from "../shared/ExportButton";
import EpisodeGrowthChart from "../show-details/EpisodeGrowthChart";
import { useEpisodeComparison } from "../store/episode-comparison";
import { useEpisodeComparisonDaily } from "../store/episode-comparison-daily";
import { useShow } from "../store/show";

const numberFormat = new Intl.NumberFormat().format;
const percentFormat = new Intl.NumberFormat(undefined, { style: "percent", signDisplay: "always" })
    .format;

export default function EpisodeComparison() {
    const { accountID, showID } = useParams();
    const s = useShow(accountID, showID) || { data: {} };
    const { data: show } = s.data ? s :  { data: {} };
    const q = useEpisodeComparison();
    const { isLoading, hasNextPage, fetchNextPage } = q;
    const { data: episodes } = q.data ? q : { data: { pages: [] } };
    const { data: dailyMeasurement = [] } = useEpisodeComparisonDaily();

    //It would probably be best to wait until we scrolled to the bottom of the page before loading more pages
    //but for now a loop works
    useEffect(() => {
        if (!isLoading && hasNextPage) fetchNextPage();
    }, [isLoading, hasNextPage]);

    const colorScale = scaleOrdinal(schemeCategory10);
    const exportClick = () =>
        (window.location = `/__/${accountID}/reports/episodecomparisonexport/${showID}`);
    return (
        <>
            <Helmet>
                <title>Compare Episodes - Podtrac</title>
            </Helmet>
            <h1 style={{ margin: "1rem 0" }}>Compare Episodes</h1>
            {episodes && (
                <EpisodeGrowthChart days={45} data={dailyMeasurement} colors={colorScale} />
            )}
            <p>
                <ExportButton onClick={exportClick} />
            </p>
            <table className="table">
                <thead>
                    <tr>
                        <th style={{ textAlign: "left" }}>Title</th>
                        <th style={{ textAlign: "right" }}>Published</th>
                        <th style={{ textAlign: "right" }}>7-Day <DownloadsLabel /></th>
                        { !show.youTubeShow ? ( <th style={{ textAlign: "right" }}>Growth vs. Avg.</th> ) : ( "" )}
                        <th style={{ textAlign: "right" }}>14-Day <DownloadsLabel /></th>
                        { !show.youTubeShow ? ( <th style={{ textAlign: "right" }}>Growth vs. Avg.</th> ) : ( "" )}
                        <th style={{ textAlign: "right" }}>30-Day <DownloadsLabel /></th>
                        { !show.youTubeShow ? ( <th style={{ textAlign: "right" }}>Growth vs. Avg.</th> ) : ( "" )}
                        <th style={{ textAlign: "right" }}>45-Day <DownloadsLabel /></th>
                        { !show.youTubeShow ? ( <th style={{ textAlign: "right" }}>Growth vs. Avg.</th> ) : ( "" )}
                        <th style={{ textAlign: "right" }}>All-Time <DownloadsLabel /></th>
                    </tr>
                </thead>
                <tbody>
                    {episodes.pages.map((group, i) =>
                        group.episodes.map((e, j) => (
                            <tr key={e.episodeID}>
                                <td style={{ textAlign: "left", display: "flex" }}>
                                    <div
                                        style={{
                                            flexShrink: 0,
                                            width: "0.8em",
                                            height: "0.8em",
                                            marginRight: "0.25em",
                                            marginTop: "0.2em",
                                            background:
                                                i == 0 && j < 10
                                                    ? colorScale(e.episodeTitle)
                                                    : "transparent"
                                        }}
                                    />
                                    <div
                                        style={{
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            maxWidth: "30ch"
                                        }}
                                    >
                                        {e.episodeTitle}
                                    </div>
                                </td>
                                <td style={{ textAlign: "right" }}>
                                    {DateTime.fromISO(e.publicationDate, {
                                        zone: "utc"
                                    }).toLocaleString(DateTime.DATE_SHORT)}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                    {e.downloads7Day ? numberFormat(e.downloads7Day) : "-"}
                                </td>
                                { !show.youTubeShow ? ( <td style={{ textAlign: "right" }}>
                                    <Growth growth={e.growth7Day} />
                                </td> ) : ( "" )}
                                <td style={{ textAlign: "right" }}>
                                    {e.downloads14Day ? numberFormat(e.downloads14Day) : "-"}
                                </td>
                                { !show.youTubeShow ? ( <td style={{ textAlign: "right" }}>
                                    <Growth growth={e.growth14Day} />
                                </td> ) : ( "" )}
                                <td style={{ textAlign: "right" }}>
                                    {e.downloads30Day ? numberFormat(e.downloads30Day) : "-"}
                                </td>
                                { !show.youTubeShow ? ( <td style={{ textAlign: "right" }}>
                                    <Growth growth={e.growth30Day} />
                                </td> ) : ( "" )}
                                <td style={{ textAlign: "right" }}>
                                    {e.downloads45Day ? numberFormat(e.downloads45Day) : "-"}
                                </td>
                                { !show.youTubeShow ? ( <td style={{ textAlign: "right" }}>
                                    <Growth growth={e.growth45Day} />
                                </td> ) : ( "" )}
                                <td style={{ textAlign: "right" }}>
                                    {e.downloadsTotal ? numberFormat(e.downloadsTotal) : "-"}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            {isLoading ? <Loading /> : null}
        </>
    );
}

function Growth({ growth }) {
    return growth ? (
        <div
            style={{
                color: growth >= 0 ? "#090" : "#c00"
            }}
        >
            {percentFormat(growth)}
        </div>
    ) : null;
}
